import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import companyLogo from "../../assets/images/nirantara_logo_new.svg";
import vajraPoweredByLogo from "../../assets/images/vajra_powered_by_logo.svg";
import VerifyOtpPage from "./VerifyOTP";
import ResetPassword from "./ReSetPassword";
import LoginSlider from "./LoginSlider";
import "./login.css";
import { api, get, post } from "../../utils/api";
import microsoftLogo from "../../assets/Login/microsoft.svg";
import MicrosoftRedirect from "../../Components/Login/MicrosoftRedirect";
import { getSubdomainLabel, getMainSiteUrl } from "../../utils/consultantDomain";

const LoginWelcomePage = () => {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [passwordAction, setPasswordAction] = useState("reset");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showMicrosoftRedirect, setShowMicrosoftRedirect] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [terms, setTerms] = useState(false);
  const [domainBranding, setDomainBranding] = useState(null);

  const passwordRef = useRef(null);

  useEffect(() => {
    const isSsoLogin = searchParams.get("ssologin") === "true";
    const emailParam = searchParams.get("email");

    if (isSsoLogin) {
      setShowMicrosoftRedirect(true);
    }
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Pre-login branding: if this page is being viewed from a consultant's
  // own subdomain (e.g. my-domain.vajra.in), show their logo before anyone
  // has even signed in. No-op on the plain Vajra domain or localhost.
  // Also: if the subdomain isn't registered to any consultant, bounce the
  // visitor to the main site rather than serving a login on a dead subdomain.
  useEffect(() => {
    const subdomain = getSubdomainLabel();
    if (!subdomain) return;

    get(`${api}/api/auth/branding-by-domain?subdomain=${encodeURIComponent(subdomain)}`)
      .then((res) => {
        if (res?.data?.exists === false) {
          window.location.href = getMainSiteUrl();
          return;
        }
        if (res?.data?.logoUrl || res?.data?.iconUrl) {
          setDomainBranding(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleForgotPassword = () => {
    setShowResetPassword(true);
    setPasswordAction("reset");
  };

  const handleBackToLogin = () => {
    setShowResetPassword(false);
    setShowOtp(false);
  };

  const handleLogin = async () => {
    setError(""); // Clear previous error
    if (!email || !password) {
      setError("Email and Password are required");
      return;
    }

    setShowResetPassword(false);

    try {
      const response = await post(`${api}/api/auth/login`, {
        email,
        password,
        subdomain: getSubdomainLabel(),
      });
      const { terms, sessionId } = response.data;
      setSessionId(sessionId);
      setTerms(terms);
      // Cached here (not just at OTP verify) so the T&C dialog, which is
      // clickable on the OTP screen before verification, already has it.
      localStorage.setItem("consultantLogoUrl", response.data.consultantLogoUrl || "");
      localStorage.setItem("consultantIconUrl", response.data.consultantIconUrl || "");
      localStorage.setItem("consultantCompanyName", response.data.consultantCompanyName || "");
      setSnackOpen({
        open: true,
        message: response?.data?.message,
        severity: "success",
      });
      setShowOtp(true);
    } catch (error) {
      console.error("Login Error:", error);
      setSnackOpen({
        open: true,
        message: error?.response?.data?.message || "Invalid email or password",
        severity: "error",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleLogin();
      e.preventDefault();
    }
  };

  console.log(passwordAction, "passwordAction");

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        margin: "0",
        padding: "0",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Left Section - Image (remains same) */}
      <Grid
        item
        sx={{
          flex: 1,
          padding: 0,
          margin: 0,
        }}
      >
        <LoginSlider />
      </Grid>

      {/* Right Section - Toggle between Login and OTP */}
      <Grid
        item
        sx={{
          display: "flex",
          padding: "3.5rem 2.5rem 2.5rem 2.5rem",
          justifyContent: "center",
          width: "33.33%",
          bgcolor: "rgba(255, 255, 255, 1)",
          position: "relative",
          // backgroundColor: "rgb(196, 230, 240)",
          overflow: "auto",
        }}
      >
        <Box sx={{ width: "100%" }}>
          {showMicrosoftRedirect ? (
            <MicrosoftRedirect
              onBack={() => setShowMicrosoftRedirect(false)}
              initialEmail={searchParams.get("email") || ""}
            />
          ) : (
            <>
              {showResetPassword ? (
                <ResetPassword
                  onBack={handleBackToLogin}
                  actionType={passwordAction}
                />
              ) : showOtp ? (
                <VerifyOtpPage
                  email={email}
                  password={password}
                  terms={terms}
                  onBack={handleBackToLogin}
                  sessionId={sessionId}
                />
              ) : (
                <>
                  <p className="main-heading">
                    WELCOME TO{" "}
                    {domainBranding?.logoUrl && domainBranding?.iconUrl && domainBranding?.companyName
                      ? domainBranding.companyName.toUpperCase()
                      : "VAJRA"}
                    !
                  </p>
                  <p className="subheading">Please Use your Registered Email</p>

                  <p className="input-label">Email ID</p>
                  <TextField
                    fullWidth
                    placeholder="Email ID"
                    variant="outlined"
                    sx={{ mb: 2, marginTop: "-0.9rem" }}
                    InputProps={{ sx: { height: "45px" } }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        passwordRef.current?.focus();
                      }
                    }}
                  />

                  <p className="input-label">Password</p>
                  <TextField
                    fullWidth
                    placeholder="Password"
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    sx={{ mb: 1 }}
                    inputRef={passwordRef}
                    InputProps={{
                      sx: { height: "45px", marginTop: "-0.9rem" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "right",
                      justifyContent: "right",
                    }}
                  >
                    <p
                      onClick={handleForgotPassword}
                      className="last-label-bold"
                      style={{ cursor: "pointer" }}
                    >
                      Forgot Password?
                    </p>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleLogin}
                    sx={{
                      bgcolor: "#EB7E0E",
                      color: "#fff",
                      textTransform: "none",
                      py: "0.5rem",
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      "&:hover": { bgcolor: "#EB7E0E" },
                    }}
                  >
                    Login
                  </Button>
                </>
              )}

              <div className="or-continue-with-container">
                <div className="or-continue-with"></div>
                <div className="or-continue-with-text">OR CONTINUE WITH</div>
                <div className="or-continue-with"></div>
              </div>

              <div
                className="social-login-button"
                onClick={() => setShowMicrosoftRedirect(true)}
              >
                <img
                  src={microsoftLogo}
                  alt="Microsoft Logo"
                  className="social-login-logo"
                />
                <div className="social-text">Sign in with Microsoft</div>
              </div>

              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  position: "absolute",
                  bottom: "1rem",
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "#838383",
                  fontSize: "12px",
                  fontWeight: "500",
                  ml: 0,
                }}
              >
                Powered by
                <span style={{ marginLeft: "0.75rem" }}>
                  <img
                    src={
                      domainBranding?.logoUrl &&
                      domainBranding?.iconUrl &&
                      domainBranding?.companyName
                        ? vajraPoweredByLogo
                        : companyLogo
                    }
                    alt="Company Logo"
                    style={{
                      height: "32px",
                      display: "inline-block",
                      marginTop: "-5px",
                    }}
                  />
                </span>
              </Typography>
            </>
          )}
        </Box>
      </Grid>

      <Snackbar
        open={snackOpen.open}
        message={snackOpen.message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LoginWelcomePage;
